import DoubleArrowOutlinedIcon from '@mui/icons-material/DoubleArrowOutlined';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import EventIcon from '@mui/icons-material/Event';
import ManageSearchIcon from '@mui/icons-material/ManageSearch';

import { redirectToG500LiteTopPage } from '../utils/redirectToG500Lite';
import { redirectToOffsegTopPage } from '../utils/redirectToOffseg';
import { CarInfo, DriverInfo, OrganizationInfo } from '../type/MasterInfoType';

export const appName = '共通ウェブポータルサービス';
export const appVersion = 'Ver.1.0.0';

export const modelName = {
	offseg: 'Offseg',
	g500Lite: 'G500Lite',
};

export const permission = {
	id: {
		super: 1,
		sales: 2,
		admin: 3,
		driver: 4,
		unknown: 99,
	},
	label: {
		super: 'super',
		sales: 'sales',
		admin: '管理者',
		driver: 'ドライバー',
		unknown: '',
	},
} as const;

export const offsegPermissionDefinition = {
	super: 'super',
	sales: 'sales',
	groupAdmin: 'G管理者',
	generalAdmin: '統括管理者',
	admin: '管理者',
	driver: 'ドライバー',
} as const;

export const g500LitePermissionDefinition = {
	super: 7, // DTENユーザ
	sales: 6, // 販社ユーザ
	systemAdmin: 5, // システム管理者モード
	admin: 4, // 管理者モード
	limitAdmin: 3, // 管理者モード(画像参照制限あり)
	driver: 2, // 乗務員モード
	limitDriver: 1, // 乗務員モード(画像参照制限あり)
} as const;

// PopUpMenuで使用するConst
export const menuItemList = [
	{
		value: modelName.offseg,
		name: modelName.offseg,
		onClick: redirectToOffsegTopPage,
		icon: DoubleArrowOutlinedIcon,
	},
	{
		value: modelName.g500Lite,
		name: modelName.g500Lite,
		onClick: redirectToG500LiteTopPage,
		icon: DoubleArrowOutlinedIcon,
	},
];

// 管理者用ドロワーメニュー
export const adminDrawerItemList = [
	{
		path: '/current-car-place',
		icon: DirectionsCarIcon,
		name: '車両現在位置一覧',
	},
	{
		path: '/car-use-result',
		icon: ManageSearchIcon,
		name: '車両利用実績一覧',
	},
	{
		path: '/event-video',
		icon: EventIcon,
		name: 'イベント映像一覧',
	},
];

// ドライバー用ドロワーメニュー
export const driverDrawerItemList = [
	{
		path: '/car-use-result',
		icon: ManageSearchIcon,
		name: '車両利用実績一覧',
	},
	{
		path: '/event-video',
		icon: EventIcon,
		name: 'イベント映像一覧',
	},
];

// 検索・検索結果一覧で使用するConst
export const header = {
	search: '検索',
	result: '【検索結果】',
	offseg: `■ ${modelName.offseg}`,
	g500Lite: `■ ${modelName.g500Lite}`,
	searchPeriod: '■ 検索期間',
	searchDate: '■ 検索日',
	maxSearchPeriodMessage: '※最大検索期間：7日',
	maxSearchNumMessage: '※Offsegの検索結果は最大200件まで表示されます。',
};

export const buttonName = {
	update: '更新',
	search: '検索',
	ok: 'OK',
};

export const organization = {
	id: 'organization',
	label: '組織',
};

export const car = {
	id: 'car',
	label: '車両',
};

export const driver = {
	id: 'driver',
	label: 'ドライバー',
};

// セレクトボックス上の「すべて」の選択肢に相当する定数
export const all = {
	id: undefined,
	label: 'すべて',
};

export const allOrganizationInfo: OrganizationInfo = {
	organizationId: undefined,
	organizationName: all.label,
};

export const allCarInfo: CarInfo = {
	carId: undefined,
	carCode: all.label,
	numberPlate: all.label,
	organizationName: '',
};

export const allDriverInfo: DriverInfo = {
	driverId: undefined,
	driverName: all.label,
};

// Offsegのログイン情報の初期値
export const initOffsegAuthInfo = {
	companyCode: '',
	userCode: '',
	password: '',
};

// G500Liteのログイン情報の初期値
export const initG500LiteAuthInfo = {
	companyId: '',
	loginId: '',
	password: '',
};

// セッションストレージのキー名：認証トークン
export const sessionStorageKeyAuthorizationToken = 'authorizationToken';

// セッションストレージのキー名：認証トークンの発行日時
export const sessionStorageKeyTokenIssuedDatetime = 'tokenIssuedDatetime';

// セッションストレージのキー名：G500Lite Webアプリへのリダイレクト実行済みかのフラグ
export const sessionStorageKeyIsRedirectedToG500Lite = 'isRedirectedToG500Lite';

// ローカルストレージのキー名：法人コード
export const localStorageKeyCompanyCode = 'companyCode';

// ローカルストレージのキー名：ユーザID
export const localStorageKeyUserCode = 'userCode';

// ダイアログで使用する
export const applicableData = '【APPLICABLE_DATA】';

export const dialogTitleLabel = {
	success: 'Success',
	warning: 'Warning',
	error: 'Error',
};

export const dialog = {
	success: {
		isOpened: false,
		title: dialogTitleLabel.success,
		text: 'データ取得に成功しました。',
	},
	systemError: {
		isOpened: true,
		title: dialogTitleLabel.error,
		text: '予期せぬエラーが発生しました。時間をあけて再度お試しください。',
	},
	apiError: {
		isOpened: true,
		title: dialogTitleLabel.error,
		text: 'データ取得に失敗しました。時間をあけて再度お試しください。',
	},
	limitOverError: {
		isOpened: true,
		title: dialogTitleLabel.error,
		text: 'データ取得に失敗しました。\nOffsegの検索条件を絞り再度実行してください。'
	},
	notExistSearchResult: {
		isOpened: true,
		title: dialogTitleLabel.warning,
		text: '検索条件に該当するデータが存在しません。\n対象データが登録されているかご確認ください。',
	},
	notExistMasterInfo: {
		isOpened: true,
		title: dialogTitleLabel.warning,
		text: `未登録のマスタデータがあります。\n(${applicableData})\nデータ登録後、ログインから再度お試しください。`,
	},
};

export const masterInfoLabel = {
	offsegOrganization: `${modelName.offseg}組織`,
	offsegCar: `${modelName.offseg}車両`,
	offsegDriver: `${modelName.offseg}ドライバー`,
	g500LiteOrganization: `${modelName.g500Lite}組織`,
	g500LiteCar: `${modelName.g500Lite}車両`,
	g500LiteDriver: `${modelName.g500Lite}ドライバー`,
};

export const coexistenceGetType = {
	currentCarPlace: 1,
	carUseResult: 2,
	eventVideo: 3,
} as const;

export const offsegGetType = {
	carAndDriverInfo: 1,
	driverInfo: 2,
} as const;

export const g500LiteGetType = {
	carAndDriverInfo: 1, // 未使用
	carInfo: 2,
	driverInfo: 3,
} as const;

// 車両利用実績一覧、イベント映像一覧で使用する
export const datePickerConst = {
	monthAndYear: 'YYYY年 MM月',
	inputFormat: 'YYYY/MM/DD',
	mask: '____/__/__',
	startDateLabel: '開始日',
	endDateLabel: '終了日',
	centerLabel: '～',
	searchDateLabel: '検索日'
};

// イベント映像一覧で使用する
export const inputPeriodErrorAlert = {
	message: '検索期間の範囲をご確認の上、再度お試しください。',
};

// 車両利用実績一覧で使用する
export const inputDateErrorAlert = {
	message: '検索日をご確認の上、再度お試しください。'
}

export const unknownLabel = '-';

export const redirectButtonLabel = {
	offseg: 'Offseg画面表示',
	g500Lite: 'G500Lite画面表示',
};

export const autocompleteOpenTextLabel = 'を開く';
